import { ReactNode } from 'react';

import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { VerticalReorderingListItem } from '@/components/vertical-reordering-list';

import { FORM_FIELD_OPTION_MAX_LENGTH } from '@/utils/constants';
import { cn } from '@/utils/styles';

import BorderTextField from '../border-text-field';
import { IOption } from './use-field-options';

type OptionRowProps = {
	option: IOption;
	index: number;
	editMode?: boolean;
	leadingControl?: ReactNode;
	showNonEditLabel?: boolean;
	displayDeleteButton: boolean;
	displayReorderIcon: boolean;
	itemClassName?: string;
	moveIconClassName?: string;
	deleteButtonClassName?: string;
	registerRef: (el: HTMLInputElement | null) => void;
	onChangeValue: (value: string) => void;
	onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
	onBlur: () => void;
	onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	onDelete: () => void;
};

const DEFAULT_MOVE_ICON_CLASS =
	'absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover/option:opacity-100 text-slate-400 hover:text-secondary';
const DEFAULT_DELETE_BUTTON_CLASS =
	'invisible absolute -right-2 h-5 w-5 p-0 text-slate-400 hover:bg-transparent group-hover/option:visible';

export function OptionRow({
	option,
	index,
	editMode,
	leadingControl,
	showNonEditLabel,
	displayDeleteButton,
	displayReorderIcon,
	itemClassName,
	moveIconClassName = DEFAULT_MOVE_ICON_CLASS,
	deleteButtonClassName = DEFAULT_DELETE_BUTTON_CLASS,
	registerRef,
	onChangeValue,
	onFocus,
	onBlur,
	onKeyDown,
	onDelete,
}: OptionRowProps) {
	return (
		<VerticalReorderingListItem
			id={option.id}
			className={cn(
				'group/option relative flex w-full items-center gap-3 p-3',
				editMode && 'w-full max-w-full pr-5',
				itemClassName
			)}
			moveIconClassName={displayReorderIcon ? moveIconClassName : undefined}
			withBoxShadow={false}
			removeDragIcon={!displayReorderIcon}
		>
			{leadingControl}

			{editMode && (
				<BorderTextField
					type="INPUT"
					value={option.name}
					placeholder={`Option ${index + 1}`}
					editMode={editMode}
					customInputClassName="text-base leading-6 flex flex-1 w-full text-primary font-normal border-none rounded-none p-0 h-fit focus-visible:ring-transparent focus-visible:ring-offset-0 truncate"
					onChangeValue={onChangeValue}
					onFocus={onFocus}
					onBlur={onBlur}
					maxLength={FORM_FIELD_OPTION_MAX_LENGTH}
					ref={(el) => registerRef(el as HTMLInputElement | null)}
					onKeyDown={onKeyDown}
					debounceDuration={0}
				/>
			)}

			{showNonEditLabel && !editMode && (
				<label
					htmlFor={option.id}
					className="cursor-pointer text-base font-normal leading-6 text-primary"
				>
					{option.name}
				</label>
			)}

			{displayDeleteButton && (
				<Button
					variant={'ghost'}
					className={deleteButtonClassName}
					onClick={onDelete}
				>
					<Trash2 className="h-5 w-5" />
				</Button>
			)}
		</VerticalReorderingListItem>
	);
}
